import { Badge, DataTable, type DataTableColumn } from "@design";
import { useTranslation } from "react-i18next";
import type { JobStatusResponse, WorkerResponse } from "@/api";
import { Text } from "@/components/Text";
import { formatDuration, formatRelativeTime } from "@/i18n/format";
import { isJobActive, isJobState, JOB_BADGE, WORKER_BADGE, workerState, workerVersion } from "./rallyStatus";

const muted = (value: string) => (
  <Text as="span" variant="body-sm" tone="tertiary">
    {value}
  </Text>
);

export function useWorkerColumns(): DataTableColumn<WorkerResponse>[] {
  const { t } = useTranslation("settings");
  return [
    {
      key: "name",
      header: t("rally.workers.columns.name"),
      mobile: "primary",
      render: (worker) => (
        <div className="flex min-w-0 flex-col text-left">
          <Text as="span" variant="body-sm" className="truncate">
            {worker.name}
          </Text>
          <code className="truncate [font:var(--type-code)] text-content-tertiary">{worker.worker_id}</code>
        </div>
      ),
    },
    {
      key: "status",
      header: t("rally.workers.columns.status"),
      render: (worker) => {
        const state = workerState(worker);
        return <Badge variant={WORKER_BADGE[state]}>{t(`rally.workers.states.${state}`)}</Badge>;
      },
    },
    {
      key: "lastSeen",
      header: t("rally.workers.columns.lastSeen"),
      render: (worker) =>
        muted(worker.last_seen_at ? formatRelativeTime(worker.last_seen_at) : t("rally.workers.never")),
    },
    {
      key: "version",
      header: t("rally.workers.columns.version"),
      render: (worker) => muted(workerVersion(worker) ?? "—"),
    },
    {
      key: "job",
      header: t("rally.workers.columns.job"),
      render: (worker) => muted(worker.current_job_id ? worker.current_job_id.slice(0, 8) : "—"),
    },
  ];
}

export function useJobColumns(): DataTableColumn<JobStatusResponse>[] {
  const { t } = useTranslation("settings");
  return [
    {
      key: "prompt",
      header: t("rally.jobs.columns.prompt"),
      mobile: "primary",
      render: (job) => (
        <div className="flex min-w-0 flex-col text-left">
          <Text as="span" variant="body-sm" className="line-clamp-2">
            {job.prompt_preview || t("rally.jobs.noPrompt")}
          </Text>
          {job.status === "failed" && job.error_message && (
            <Text as="span" variant="caption" tone="error" className="line-clamp-2">
              {job.error_message}
            </Text>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: t("rally.jobs.columns.status"),
      render: (job) =>
        isJobState(job.status) ? (
          <Badge variant={JOB_BADGE[job.status]}>{t(`rally.jobs.states.${job.status}`)}</Badge>
        ) : (
          <Badge>{job.status}</Badge>
        ),
    },
    {
      key: "worker",
      header: t("rally.jobs.columns.worker"),
      render: (job) => muted(job.worker_id ? job.worker_id.slice(0, 16) : "—"),
    },
    {
      key: "duration",
      header: t("rally.jobs.columns.duration"),
      render: (job) => {
        if (job.elapsed_seconds === null) return muted("—");
        const key = isJobActive(job) ? "rally.jobs.elapsed" : "rally.jobs.took";
        return muted(t(key, { duration: formatDuration(job.elapsed_seconds) }));
      },
    },
    {
      key: "created",
      header: t("rally.jobs.columns.created"),
      render: (job) => muted(job.created_at ? formatRelativeTime(job.created_at) : "—"),
    },
  ];
}

export function WorkersTable({ workers, loading }: Readonly<{ workers: WorkerResponse[]; loading: boolean }>) {
  const { t } = useTranslation("settings");
  return (
    <DataTable
      aria-label={t("rally.workers.title")}
      columns={useWorkerColumns()}
      rows={workers}
      rowKey={(worker) => worker.id}
      loading={loading}
      emptyMessage={t("rally.workers.empty")}
    />
  );
}

export function JobsTable({ jobs, loading }: Readonly<{ jobs: JobStatusResponse[]; loading: boolean }>) {
  const { t } = useTranslation("settings");
  return (
    <DataTable
      aria-label={t("rally.jobs.title")}
      columns={useJobColumns()}
      rows={jobs}
      rowKey={(job) => job.id}
      loading={loading}
      emptyMessage={t("rally.jobs.empty")}
    />
  );
}
